import React, { useEffect, useState, version } from "react";
import { useNavigate } from "react-router-dom";
import Timer from "./Timer";
import axios from "axios";
import { REACT_APP_API_URL } from "../config";
import Swal from "sweetalert2";
import Checkbox from "../utils/Checkbox";
const DEEPGRAM_API_KEY = process.env.REACT_APP_DEEPGRAM_API_KEY;

const LiveSTT = () => {
  const [transcripts, setTranscripts] = useState("");
  const [audioStream, setAudioStream] = useState(null);
  const [selectedMicrophone, setSelectedMicrophone] = useState("");
  const [microphoneOptions, setMicrophoneOptions] = useState([]);
  const [mediaRecorder, setMediaRecorder] = useState(null);
  const [audioChunks, setAudioChunks] = useState([]);
  const [connectStatus, setConnectStatus] = useState("Connecting...");
  const [showConfig, setShowConfig] = useState(false);
  const [configure, setConfigure] = useState({
    diarize: true, // Speaker identification
    dictation: true, //Converts spoken dictation commands into their corresponding punctuation marks (punctuation must be true)
    filler_words: false, // Disable filler words
    interim_results: false, // interim transcription results here Final results only
    multichannel: false, // transcribes each channel in submitted audio independently
    numerals: true, // Convert numbers to digits
    profanity_filter: false, // No profanity filter
    punctuate: true, // Automatic punctuation
    smart_format: true, // Smart formatting for dates, times, and other entities
    utterances: true, // segmentation of speech into distinct utterances
    vad_events: true, // Voice activity detection
    channels: 1, // Number of audio channels
    diarize_version: "latest", // Diarization version
    encoding: "linear16", // Audio encoding format
    endpointing: 300, // End of utterance in ms
    language: "en-US", //  Language code for the transcription
    model: "nova-2", // Speech recognition model
    version: 'latest',
    redact: "ssn", // Redact SSNs
    replace: "badword:goodword", // Replace words
    sample_rate: 16000, // Sample rate of the audio in Hz
    utterance_end_ms: 200, // Time in milliseconds to wait after speech ends before finalizing the utterance
  });

  const navigate = useNavigate();

  const onBackHandler = (stream) => {
    navigate("/");
    stream?.getTracks()?.forEach((track) => {
      if (track.readyState === "live" && track.kind === "audio") {
        track.stop();
      }
    });
  };

  const resetValuesHanler = (stream) => {
    stream?.getTracks()?.forEach((track) => {
      if (track.readyState === "live" && track.kind === "audio") {
        track.stop();
      }
    });

    setConnectStatus("Disconnected");
    setAudioStream(null);
    setSelectedMicrophone("");
    setAudioChunks([]);
    setMediaRecorder();
  };

  const uploadFileGenerateTranscript = (stream) => {
    try {
      const blob = new Blob(audioChunks, { type: "audio/webm" });
      const formData = new FormData();
      formData.append("file", blob, "conversation.webm");
      axios.post(`${REACT_APP_API_URL}/api2/upload`, formData, {
        headers: new Headers({
          "ngrok-skip-browser-warning": "69420",
        }),
      });
    } catch (error) {
      const errorMessage = `${error?.response?.data?.error}`;
      Swal.fire({
        icon: "error",
        title: "Oops...",
        text: error?.response?.data?.error
          ? errorMessage
          : `status code: ${
              error?.response?.status ? error?.response?.status : "server down/"
            } \n something went wrong`,
      });
    } finally {
      resetValuesHanler(stream);
    }
  };

  const saveAudioFile = (stream) => {
    try {
      const blob = new Blob(audioChunks, { type: "audio/webm" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.style.display = "none";
      a.href = url;
      a.download = "conversation.webm";
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      const errorMessage = `Something went wrong while saving the audio`;
      Swal.fire({
        icon: "error",
        title: "Oops...",
        text: errorMessage,
      });
    } finally {
      resetValuesHanler(stream);
    }
  };

  useEffect(() => {
    const getMicrophoneOptions = async () => {
      const devices = await navigator.mediaDevices.enumerateDevices();
      const microphoneOptions = devices
        .filter((device) => device.kind === "audioinput")
        .map((device) => ({
          value: device.deviceId,
          label: device.label,
        }));
      setMicrophoneOptions(microphoneOptions);
    };

    getMicrophoneOptions();

    if (!DEEPGRAM_API_KEY) {
      console.error(
        "No API key found. Please set REACT_APP_DEEPGRAM_API_KEY in your .env file."
      );
      return;
    }
  }, []);

  useEffect(() => {
    if (!selectedMicrophone) {
      return;
    }
    setTranscripts("");
    // WebSocket URL with diarization and smart formatting
    const socket = new WebSocket(
      `wss://api.deepgram.com/v1/listen?diarize=${configure.diarize}&model=${configure.model}&punctuate=${configure.punctuate}&utterances=${configure.utterances}`,
      ["token", DEEPGRAM_API_KEY],
    );


    // keep alive solve the problem when audio is silent and helps to reduce the cost while making the open connection in silent periods 
    // didnot checked yet
    // setInterval(() => {
    //   const keepAliveMsg = JSON.stringify({ type: "KeepAlive" });
    //   socket.send(keepAliveMsg);
    //   console.log("Sent KeepAlive message");
    // }, 5000);

    socket.onopen = () => {
      console.log("WebSocket connected to Deepgram");
      setConnectStatus("Connected");
      const latency = 1000 * 1;

      // Start MediaRecorder to capture audio
      navigator.mediaDevices
        .getUserMedia({
          audio: {
            sampleRate: 16000,
            channelCount: 1,
            deviceId: selectedMicrophone,
          },
        })
        .then((stream) => {
          setAudioStream(stream);
          const recorder = new MediaRecorder(stream, {
            mimeType: "audio/webm;codecs=opus",
          });
          setMediaRecorder(recorder);

          recorder.addEventListener("dataavailable", (event) => {
            // console.log("Sending audio chunk at:", Date.now());
            if (event.data.size > 0) {
              setAudioChunks((prev) => [...prev, event.data]);
              if (socket.readyState === 1) {
                socket.send(event.data);
              }
            }
          });
          recorder.start(latency); // Send audio data every 10ms
        })
        .catch(() => {
          alert("Plug in the microphone!");
          navigate("/");
        });
    };

    socket.onmessage = (message) => {
      // console.log("Received transcript at:", Date.now());
      const received = JSON.parse(message.data);

      if (received.channel?.alternatives?.[0]?.transcript) {
        const transcript = received.channel.alternatives[0].transcript;

        if (received.is_final) {
          const speaker = received.channel.alternatives[0].words?.[0]?.speaker;
          const speakerLabel =
            speaker !== undefined ? `Speaker ${speaker}: ` : "";
          setTranscripts((prev) => prev + `${speakerLabel}${transcript}\n\n`);
        } else {
          // Display interim results
          const speaker = received.channel.alternatives[0].words?.[0]?.speaker;
          const speakerLabel =
            speaker !== undefined ? `Speaker ${speaker}: ` : "";
          const interimText = `${speakerLabel}${transcript}`;
          document.getElementById("interim").textContent = interimText; // Show interim result
        }
      }
    };

    socket.onclose = () => {
      console.log("WebSocket connection closed");
      setConnectStatus("Disconnected");
    };

    socket.onerror = (error) => {
      Swal.fire({
        icon: "error",
        title: "Oops...",
        text: `WebSocket error: ${error}`,
      });
    };

    return () => {
      if (mediaRecorder && mediaRecorder.state !== "inactive") {
        mediaRecorder.stop();
      }
      if (audioStream) {
        audioStream?.getTracks()?.forEach((track) => track?.stop());
      }
      socket.close();
    };
  }, [selectedMicrophone]);

  const handleMicrophoneChange = (event) => {
    setSelectedMicrophone(event.target.value);
  };

  return (
    <div>
      <h1>Live Transcription</h1>
      <select
        className="btn"
        value={selectedMicrophone}
        onChange={handleMicrophoneChange}
        disabled={connectStatus === "Connected" ? true : false}
      >
        <option value="">Select Microphone</option>
        {microphoneOptions?.map((option) => (
          <option key={option?.value} value={option?.value}>
            {option?.label}
          </option>
        ))}
      </select>

      <button className="btn back" onClick={() => onBackHandler(audioStream)}>
        Back
      </button>

      <div className="status-container">
        <div className="status">{connectStatus}</div>
        <div>
          {/* <div className="btn refresh" onClick={() => setShowConfig((prev) => !prev)}>
            Config
          </div> */}
          {showConfig && (
            <div>
              {Object.keys(configure).map((lable) => {
                if (typeof configure[lable] === "boolean") {
                  return (
                    <Checkbox
                      key={lable}
                      label={lable}
                      isChecked={configure[lable]}
                      defaultValue={configure[lable]}
                      setterFunction={setConfigure}
                    />
                  );
                } else {
                  return (
                    <div className="input-config">
                      <label htmlFor={lable}>{lable}:&nbsp;&nbsp;</label>
                      <input
                        type="text"
                        key={lable}
                        name={lable}
                        value={configure[lable]}
                        min={500}
                        className="input-num"
                        max={16384}
                        onChange={(e) => {
                          const { name, value } = e.target;
                          setConfigure((prev) => ({
                            ...prev,
                            [name]: value,
                          }));
                        }}
                      />
                    </div>
                  );
                }
              })}
            </div>
          )}
        </div>
        {connectStatus === "Connected" ? (
          <div className="live-container1">
            <Timer />
            <div
              onClick={() => resetValuesHanler(audioStream)}
              className="save-audio"
            >
              <img src="/stop-button.png" alt="stop" height={"35px"} />
            </div>
            <div
              onClick={() => saveAudioFile(audioStream)}
              className="save-audio"
            >
              <img src="/save_audio.png" alt="save" height={"35px"} />
            </div>
            <div
              onClick={() => uploadFileGenerateTranscript(audioStream)}
              className="save-audio"
            >
              <img src="/upload.png" alt="upload" height={"35px"} />
            </div>
          </div>
        ) : (
          <></>
        )}
      </div>
      <pre
        className="transcript-live"
        style={{ whiteSpace: "pre-wrap", fontFamily: "sans-serif" }}
      >
        <div className="convesation-live">Convesation</div>
        {transcripts}
      </pre>
    </div>
  );
};

export default LiveSTT;
